import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, ChevronRight, Globe } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import LiveChatWidget from "@/components/LiveChatWidget";

const WA_URL = "https://wa.me/918500914557?text=" +
  encodeURIComponent("Hi Jain Infotech, I'd like to get a quote.");

function JainLogo({ uid = "a", scale = 1 }) {
  const size = Math.round(52 * scale);
  return (
    <div className="bg-white rounded-md p-1 flex items-center justify-center flex-shrink-0">
      <img
        src="https://customer-assets.emergentagent.com/job_jain-infotech/artifacts/lvv001gz_JAIN%20INFOTECH%20LOGO-1.jpg"
        alt="Jain Infotech Logo"
        style={{ width: size, height: size, objectFit: "contain" }}
      />
    </div>
  );
}

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/industries", label: "Industries" },
  { to: "/why-choose-us", label: "Why Us" },
  { to: "/contact", label: "Contact" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => setOpen(false), [loc]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 bg-[#2D3748] transition-shadow duration-300 ${scrolled ? "shadow-2xl" : "shadow-md"}`}
    >
      {/* Top contact bar */}
      <div className="bg-[#1A2535] border-b border-white/10 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 py-1.5 flex justify-end gap-6">
          <a href="tel:9908131050" data-testid="header-phone1" className="flex items-center gap-1.5 text-gray-400 text-xs hover:text-white transition-colors">
            <Phone size={11} /> +91 9908131050
          </a>
          <a href="tel:8500914557" data-testid="header-phone2" className="flex items-center gap-1.5 text-gray-400 text-xs hover:text-white transition-colors">
            <Phone size={11} /> +91 8500914557
          </a>
          <a href="mailto:jaininfotech22@gmail.com" data-testid="header-email" className="flex items-center gap-1.5 text-gray-400 text-xs hover:text-white transition-colors">
            <Mail size={11} /> jaininfotech22@gmail.com
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <JainLogo uid="hdr" />
          <div className="leading-none">
            <p className="font-franklin text-white text-xl tracking-widest leading-none">JAIN</p>
            <p className="font-franklin text-[#0F62FE] text-xs tracking-[0.45em] mt-0.5">INFOTECH</p>
            <p className="text-[9px] text-gray-400 uppercase tracking-[0.2em] mt-1 hidden sm:block">Corporate IT Distribution</p>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7" data-testid="desktop-nav">
          {NAV.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={`text-sm font-medium transition-colors relative pb-0.5 ${
                loc.pathname === to ? "text-[#00BCD4]" : "text-gray-300 hover:text-white"
              }`}
            >
              {label}
              {loc.pathname === to && (
                <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#00BCD4] rounded-full" />
              )}
            </Link>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="header-get-quote-btn"
          className="hidden md:inline-flex items-center gap-2 bg-[#0F62FE] hover:bg-[#0052CC] text-white text-sm font-semibold px-5 py-2.5 transition-all duration-200 hover:shadow-lg"
        >
          Get a Quote <ChevronRight size={14} />
        </Link>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden text-white p-2"
          aria-label="Toggle navigation"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#1A2535] border-t border-white/10 px-6 py-4" data-testid="mobile-nav">
          {NAV.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`block py-3 text-sm font-medium border-b border-white/5 transition-colors ${
                loc.pathname === to ? "text-[#00BCD4]" : "text-gray-300"
              }`}
            >
              {label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="block mt-4 bg-[#0F62FE] text-white text-center py-3 font-semibold text-sm"
          >
            Get a Quote
          </Link>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#2D3748] text-white">
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <JainLogo uid="ftr" scale={0.78} />
              <div>
                <p className="font-franklin text-white text-lg tracking-widest leading-none">JAIN</p>
                <p className="font-franklin text-[#0F62FE] text-[10px] tracking-[0.45em] mt-0.5">INFOTECH</p>
                <p className="text-[8px] text-gray-400 uppercase tracking-[0.2em] mt-1">Corporate IT Distribution</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              Trusted IT Infrastructure &amp; Surveillance Solutions Partner since 2017.
            </p>
            <a
              href={WA_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp-btn"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb655] text-white text-xs font-semibold px-4 py-2 transition-colors"
            >
              <FaWhatsapp size={14} /> WhatsApp Us
            </a>
          </div>

          <div>
            <h4 className="font-bold text-white text-xs uppercase tracking-widest mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {[
                { to: "/", label: "Home" },
                { to: "/about", label: "About Us" },
                { to: "/products", label: "Products" },
                { to: "/industries", label: "Industries" },
                { to: "/why-choose-us", label: "Why Choose Us" },
                { to: "/contact", label: "Contact" },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="text-gray-400 hover:text-[#00BCD4] text-sm transition-colors flex items-center gap-1.5">
                    <ChevronRight size={11} /> {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-white text-xs uppercase tracking-widest mb-4">Product Lines</h4>
            <ul className="space-y-2.5">
              {["Cables & Connectivity","Peripherals & Audio","Networking Infrastructure","CCTV & Surveillance","KVM & Enterprise Solutions"].map((item) => (
                <li key={item}>
                  <Link to="/products" className="text-gray-400 hover:text-[#00BCD4] text-sm transition-colors flex items-center gap-1.5">
                    <ChevronRight size={11} /> {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-white text-xs uppercase tracking-widest mb-4">Contact Us</h4>
            <ul className="space-y-3.5">
              <li className="flex items-start gap-2.5 text-gray-400 text-sm">
                <MapPin size={15} className="mt-0.5 shrink-0 text-[#00BCD4]" />
                <span>Shop No. 22, Chenoy Trade Center, Park Lane, Kalasiguda, Secunderabad – 500003</span>
              </li>
              <li>
                <a href="tel:9908131050" className="flex items-center gap-2.5 text-gray-400 hover:text-white text-sm transition-colors">
                  <Phone size={13} className="text-[#00BCD4]" /> +91 9908131050
                </a>
              </li>
              <li>
                <a href="tel:8500914557" className="flex items-center gap-2.5 text-gray-400 hover:text-white text-sm transition-colors">
                  <Phone size={13} className="text-[#00BCD4]" /> +91 8500914557
                </a>
              </li>
              <li>
                <a href="mailto:jaininfotech22@gmail.com" className="flex items-center gap-2.5 text-gray-400 hover:text-white text-sm transition-colors break-all">
                  <Mail size={13} className="text-[#00BCD4] shrink-0" /> jaininfotech22@gmail.com
                </a>
              </li>
              <li>
                <a
                  href="https://www.jaininfotech.co.in"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-site"
                  className="flex items-center gap-2.5 text-gray-400 hover:text-white text-sm transition-colors break-all"
                >
                  <Globe size={13} className="text-[#00BCD4] shrink-0" /> www.jaininfotech.co.in
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 bg-[#1A2535]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-2">
          <p className="text-gray-500 text-xs">&copy; {new Date().getFullYear()} Jain Infotech. All rights reserved.</p>
          <p className="text-gray-500 text-xs">GST: 36CHTPD6007L1Z0 &nbsp;|&nbsp; Secunderabad, Telangana</p>
        </div>
      </div>
    </footer>
  );
}

function WhatsAppButton() {
  return (
    <a
      href={WA_URL}
      target="_blank"
      rel="noreferrer"
      data-testid="whatsapp-float-button"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb655] text-white font-semibold px-4 py-3 rounded-full shadow-xl transition-all duration-200 hover:shadow-2xl hover:scale-105 group"
    >
      <FaWhatsapp size={22} />
      <span className="max-w-0 overflow-hidden group-hover:max-w-[120px] whitespace-nowrap transition-all duration-300 text-sm">
        Chat for Quote
      </span>
    </a>
  );
}

export default function Layout({ children }) {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <WhatsAppButton />
      <LiveChatWidget />
    </div>
  );
}
